/**
 * #391 — Purchase da Meta pelo navegador, redundante com a Conversions API.
 *
 * O backend manda o mesmo Purchase pela CAPI quando o webhook da AppMax aprova
 * (`payments.service.ts`, bloco `isRenewal`). Com o MESMO `eventID` a Meta conta
 * um só; sem ele conta dois. Por isso o navegador só dispara o que o servidor
 * também conta, com o valor que o servidor manda:
 *  - id: `order.appmaxOrderId` — espelho de `purchaseEventId()` em
 *    `backend/src/integrations/platforms/facebook/meta-capi-payload.ts`;
 *  - valor: `payment.amount` da resposta — é o `valueToSend` do servidor (no
 *    cartão o item da order e o payment gravam o mesmo valor, inclusive no
 *    fallback 1/12; no PIX o servidor já manda `payment.amount`).
 * Renovação e recusa não chamam isto — decisão de quem chama.
 *
 * Nunca lança: `fbq` ausente (adblock) ou dado faltando = segue sem Purchase.
 */

/** event_id do Purchase — o backend manda o mesmo (`purchase_<appmaxOrderId>`). */
export const purchaseEventId = (appmaxOrderId: string | number): string => `purchase_${appmaxOrderId}`;

// Polling e WebSocket do PIX podem confirmar a mesma compra mais de uma vez.
const disparados = new Set<string>();

export function trackMetaPurchase(params: {
  appmaxOrderId?: string | number | null;
  value?: unknown;
}): boolean {
  try {
    const orderId = params.appmaxOrderId == null ? '' : String(params.appmaxOrderId).trim();
    const value = Number(params.value);
    // Sem id não há dedup; sem valor o evento diverge do servidor. Melhor só o servidor.
    if (!orderId || !Number.isFinite(value) || value <= 0) return false;

    const eventID = purchaseEventId(orderId);
    if (disparados.has(eventID) || typeof window.fbq !== 'function') return false;

    window.fbq(
      'track',
      'Purchase',
      { value, currency: 'BRL', content_type: 'product', content_ids: [orderId] },
      { eventID },
    );
    disparados.add(eventID);
    return true;
  } catch {
    return false;
  }
}
